import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authApi } from '../api/authApi'
import { PKCE_VERIFIER_KEY } from '../utils/pkce'

function getFunnyError(raw: string): string {
  const lower = raw.toLowerCase()
  if (lower.includes('cancel') || lower.includes('denied') || lower.includes('access_denied'))
    return "Changed your mind? No judgment here! 👀 Come back when you're ready."
  if (lower.includes('duplicate') || lower.includes('already exists') || lower.includes('conflict'))
    return "Plot twist — looks like you already have an account! 😮 Try a different sign-in method or head to the login page."
  if (lower.includes('network') || lower.includes('fetch') || lower.includes('timeout') || lower.includes('failed to fetch') || lower.includes('econn') || lower.includes('enotfound') || lower.includes('internal server') || lower.includes('server_error') || lower.includes('network_error'))
    return "Our servers must be doing yoga right now. 🧘 Give it a moment and try again!"
  if (lower.includes('pkce') || lower.includes('verif') || lower.includes('secret handshake'))
    return "Your secret handshake got scrambled somewhere along the way. 🤝 Head back to the login page and try again."
  if (lower.includes('no authoriz') || lower.includes('no code'))
    return "Hmm, your magic login code went on holiday without telling us. 🏖️ Try again from the home page!"
  return "Something mysterious happened on the way to signing you in. 🕵️ Our best (and only) engineers are probably on it. Try again?"
}

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginOAuth } = useAuth()
  const [error, setError] = useState('')
  const exchangeStarted = useRef(false)

  useEffect(() => {
    const code = searchParams.get('code')
    const errorParam = searchParams.get('error')
    const errorDesc = searchParams.get('error_description')
    const state = searchParams.get('state') || 'google'
    const provider = state === 'microsoft' ? 'microsoft' : state === 'apple' ? 'apple' : state === 'facebook' ? 'facebook' : 'google'

    if (!code) {
      if (errorParam || errorDesc) {
        setError(getFunnyError(errorDesc || errorParam || 'cancelled'))
      } else {
        setError(getFunnyError('no code'))
      }
      return
    }

    // Prevent double execution (React Strict Mode runs effects twice in dev)
    if (exchangeStarted.current) return
    exchangeStarted.current = true

    const redirectUri = `${window.location.origin}/auth/callback`
    const codeVerifier = provider === 'microsoft' ? sessionStorage.getItem(PKCE_VERIFIER_KEY) : undefined
    if (provider === 'microsoft') {
      sessionStorage.removeItem(PKCE_VERIFIER_KEY)
      if (!codeVerifier) {
        setError(getFunnyError('pkce'))
        return
      }
    }

    const doExchange = async () => {
      try {
        const data =
          provider === 'apple'
            ? await authApi.exchangeAppleCode(code, redirectUri)
            : provider === 'microsoft'
            ? await authApi.exchangeMicrosoftCode(code, redirectUri, undefined, codeVerifier ?? undefined)
            : provider === 'facebook'
            ? await authApi.exchangeFacebookCode(code, redirectUri)
            : await authApi.exchangeGoogleCode(code, redirectUri)
        loginOAuth(data)
        const userRole = data.user?.role
        if (userRole === 'admin') {
          navigate('/admin', { replace: true })
          return
        }
        if (userRole === 'data_operator') {
          navigate('/dataentry', { replace: true })
          return
        }
        if (userRole === 'broker') {
          navigate('/broker', { replace: true })
          return
        }
        const pending = sessionStorage.getItem('pending_lead_capture')
        if (pending) {
          try {
            const { selectedLoan } = JSON.parse(pending)
            sessionStorage.removeItem('pending_lead_capture')
            navigate('/customer', { replace: true, state: { openLeadModal: true, selectedLoan } })
          } catch {
            sessionStorage.removeItem('pending_lead_capture')
            const returnTo = sessionStorage.getItem('oauth_return_to') || '/customer'
            sessionStorage.removeItem('oauth_return_to')
            navigate(returnTo, { replace: true })
          }
        } else {
          const returnTo = sessionStorage.getItem('oauth_return_to') || '/customer'
          sessionStorage.removeItem('oauth_return_to')
          navigate(returnTo, { replace: true })
        }
      } catch (e) {
        setError(getFunnyError(e instanceof Error ? e.message : ''))
      }
    }
    doExchange()
  }, [searchParams, loginOAuth, navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center p-8 bg-white rounded-lg shadow">
          <div className="text-4xl mb-4">😬</div>
          <h2 className="text-xl font-semibold text-red-500 mb-4">Uh oh! Sign-in hit a speed bump.</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a href="/" className="text-primary-600 hover:text-primary-700 font-medium">
            Return to home
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-gray-600">Signing you in...</div>
    </div>
  )
}
